/**
 * Stoxly REST data spine for the admin panel — the single backendv2
 * client/query layer, mirroring `apps/web/src/core/api/stoxly.ts`.
 *
 * Wraps the generated, Zod-validated `@stoxly/api-client` (ky transport) with
 * `@stoxly/api-query`'s TanStack option factories:
 *
 *   useQuery(stoxly.queries.admin.stats);
 *   useMutation(stoxly.mutations.admin.impersonateUser);
 *
 * Auth rides the SAME Supabase session as the rest of the app: a single token
 * cache below (written by the auth store on sync/logout and by the ky-runtime
 * after a 401 refresh), plus a one-shot session warm-up so early requests
 * never race the async session hydration.
 */
import { createStoxlyClient, type Body, type Query, type Response } from '@stoxly/api-client';
import { createStoxlyQueries } from '@stoxly/api-query';
import { supabase } from '@/lib/supabase';
import { configService } from '@/services/config';

let cachedAccessToken: string | null = null;

// A single in-flight session warm-up: the token cache hydrates ASYNCHRONOUSLY
// from the stored Supabase session, so an early request could otherwise go out
// with no Authorization header and 401.
let sessionWarmup: Promise<void> | null = null;
function warmAccessToken(): Promise<void> {
  sessionWarmup ??= supabase.auth
    .getSession()
    .then(({ data: { session } }) => {
      if (session?.access_token) updateAccessToken(session.access_token);
    })
    .catch(() => {
      // No session / transient failure: leave the cache null (logged-out path).
    });
  return sessionWarmup;
}

void warmAccessToken();

/** Set the cached Supabase access token — the single token-cache authority. */
export function updateAccessToken(token: string | null): void {
  cachedAccessToken = token;
}

/** The current Supabase access token (or null) — the download-client seam. */
export function getAccessToken(): string | null {
  return cachedAccessToken;
}

/**
 * Async token resolver passed to the api-client as `getToken`. Asks auth-js for
 * the session on EVERY request: `getSession()` serves from memory and
 * proactively refreshes an expiring token (single-flighted, multi-tab safe), so
 * requests never carry a dead token — a backgrounded admin tab's pollers keep
 * riding fresh tokens instead of replaying an expired one. Falls back to the
 * cached token on a transient auth-js throw.
 */
async function resolveAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token ?? null;
    updateAccessToken(token);
    return token;
  } catch {
    return cachedAccessToken;
  }
}

// api-client wants ORIGIN only (generated paths already include /api), but
// configService.getApiUrl() returns `<origin>/api`. Strip the suffix.
function resolveApiOrigin(): string {
  return configService.getApiUrl().replace(/\/api\/?$/, '');
}

/**
 * The generated `@stoxly/api-client` instance — the SOLE REST transport.
 * Use `stoxlyClient.opId(args)` for imperative calls outside a query context.
 */
export const stoxlyClient = createStoxlyClient({
  baseUrl: resolveApiOrigin(),
  getToken: resolveAccessToken,
  refresh: {
    // Single concurrent refresh is enforced by the ky-runtime; this performs
    // one refreshSession() and updates the cache the retried request re-reads.
    refreshToken: async () => {
      const { data } = await supabase.auth.refreshSession();
      const token = data.session?.access_token ?? null;
      updateAccessToken(token);
      return token;
    },
    // No session after refresh = truly expired: sign out and land on /login
    // (the auth store's SIGNED_OUT handler performs the route change).
    onSessionExpired: async () => {
      if (!window.location.pathname.includes('/login')) {
        await supabase.auth.signOut();
      }
    },
  },
});

export const stoxly = createStoxlyQueries(stoxlyClient);

// Alias-keyed request/response types, re-exported so consumers never
// hand-duplicate DTO names: Body<'campaignsCreate'>, Response<'adminGetUsers'>.
export type { Body, Query, Response };
